import { FC, useEffect } from 'react';
import {
  PaymentMethodDisplayOptions,
  Box,
  Card,
  withStyles,
  formatPhoneNumberWithParentheses,
  InteracWordmark,
  capitalizeFirstLetter,
  BankAccountConnectionType,
  AccountType,
  TransactionStatus as TransactionStatusType,
  TransactionType,
  AcceptedCardBrand,
  formattedCurrency,
  capitalizeFirstLetterOfWords,
  removeWhiteSpaceURI,
  TransactionsLists,
  Skeleton,
  GetColorStatus,
} from 'vopay';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UtilButton, Title, CopyButton, DetailsRows, TransactionTypeLabel, TransactionStatus } from 'UI';
import { addDays, format } from 'date-fns';
import { useI18nLabels } from '@/utils/useI18nLabels';
import { useToast } from '@/providers/Toast.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import useElinx, { ElinxItem } from './useElinx';
import ApprovalHistoryDetailsCard from '@/components/Transaction/TransactionsHistory/Details/Card/ApprovalHistoryDetailsCard';
import ElinxStatusStepper from './ElinxStatusStepper';
import styles from './ElinxDetails.module.scss';

const cn = withStyles(styles);

type ElinxDetailsProps = {
  details: ElinxItem;
  // The eLinx request's funding parent transaction (when the request was pre-funded)
  fundingParentTransaction?: TransactionsLists;
  isFetchingFundingParent?: boolean;
};

const ElinxDetails: FC<ElinxDetailsProps> = ({ details, fundingParentTransaction, isFetchingFundingParent }) => {
  const history = useHistory();
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'ElinxHistory.Details' });
  const { t: tGlobal } = useTranslation('translations');
  const { frequencyLabelMapper } = useI18nLabels();

  const {
    documentLink,
    documentName,
    transaction,
    scheduledTransaction,
    fetchTransaction,
    fetchScheduledTransaction,
    fetchDocument,
  } = useElinx();

  const flexiblePaymentOptions = Object.values(details.FlexiblePaymentOptions);

  const isScheduled = details.ELinxRequestType === 'scheduled' || Boolean(details.ScheduledPaymentsID);

  const {
    response: { clientAccountsEnabled },
  } = useClientData();

  const { onSubNavClick } = useNavMenu();

  const getTransaction = async () => {
    try {
      const startDate = format(details.RequestDate, 'yyyy-MM-dd');
      const endDate = format(addDays(startDate, 30), 'yyyy-MM-dd');

      await fetchTransaction({
        TransactionID: details.TransactionID || '',
        StartDateTime: startDate,
        EndDateTime: endDate,
      });
    } catch (err) {
      console.error(err);

      toast.neutral(
        t('fetchingTransactionError', {
          defaultValue: 'Failed to fetch eLinx transaction information',
        }),
      );
    }
  };

  const getScheduledTransaction = async () => {
    try {
      await fetchScheduledTransaction({
        ScheduledTransactionID: String(details.ScheduledPaymentsID),
      });
    } catch {
      toast.neutral(
        t('fetchingScheduledError', {
          defaultValue: 'Failed to fetch scheduled transaction information',
        }),
      );
    }
  };

  const getDocument = async () => {
    try {
      await fetchDocument({
        DocumentID: details.DocumentID,
      });
    } catch (err) {
      console.error(err);

      toast.neutral(
        t('fetchingDocumentError', {
          defaultValue: 'Failed to fetch the invoice',
        }),
      );
    }
  };

  useEffect(() => {
    if (details.TransactionID !== '') {
      getTransaction();
    }

    if (details.DocumentID !== '') {
      getDocument();
    }

    if (isScheduled && details.Status === 'completed' && Boolean(details.ScheduledPaymentsID)) {
      getScheduledTransaction();
    }
  }, [details]);

  const BANKING_RAILS = ['eft', 'vopayinstant', 'ach', 'rtp', 'fedwire'];
  type BankingPaymentRail = (typeof BANKING_RAILS)[number];

  function isBankingPaymentRail(value: string): value is BankingPaymentRail {
    return BANKING_RAILS.includes(value as BankingPaymentRail);
  }

  const bankingPaymentRails = details.BankingPaymentRail.split(',')
    .map((r) => r.trim())
    .filter(isBankingPaymentRail);

  const statusMap: Record<string, TransactionStatusType> = {
    pending: TransactionStatusType.PENDING,
    completed: TransactionStatusType.COMPLETE,
    cancelled: TransactionStatusType.CANCELLED,
    declined: TransactionStatusType.DECLINED,
    waiting: TransactionStatusType.WAITING,
    'pending approval': TransactionStatusType.PENDING_APPROVAL,
  };

  const showCardSettings =
    details.RequestPaymentMethodType.includes('credit') || details.RequestPaymentMethodType.includes('debitcard');

  return (
    <>
      <Card className={cn('summaryCard')}>
        <Box gapSize="24px">
          <Box flx="col" gapSize="24px" className={cn('summaryMain')}>
            <Box jc="sb" ai="center">
              <Link
                to={
                  clientAccountsEnabled
                    ? `/client-accounts/${details.ClientAccountID}`
                    : `/contacts/${details.ContactID}`
                }
                target="_blank"
              >
                <UtilButton className={cn('receiverButton')}>{details.ReceiverName}</UtilButton>
              </Link>
            </Box>

            <div className={cn('summaryContainer')}>
              {details.eLinxType !== 'eConnect' && (
                <Box flx="col" gapSize="5px">
                  <Title level="5" emphasized={false} className={cn('grey')}>
                    {details.eLinxType === 'ePay'
                      ? t('amountSent', { defaultValue: 'Amount Sent' })
                      : t('amountRequested', { defaultValue: 'Amount Requested' })}
                  </Title>

                  <Title level="4">
                    {details.Currency} {formattedCurrency(String(details.Amount))}
                  </Title>
                </Box>
              )}

              <div className={cn('idContainer')}>
                {isFetchingFundingParent && (
                  <Box flx="col" gapSize="6px">
                    <div className={cn('row')}>{t('parentID', { defaultValue: 'Parent ID' })}</div>
                    <Skeleton width={70} height={16} />
                  </Box>
                )}

                {fundingParentTransaction && (
                  <Box flx="col" gapSize="6px">
                    <div className={cn('row')}>{t('parentID', { defaultValue: 'Parent ID' })}</div>

                    <Box gapSize="5px">
                      <UtilButton
                        className={cn('idButton')}
                        onClick={() => {
                          window.open(
                            `/transactions-history/details/${removeWhiteSpaceURI(fundingParentTransaction.TransactionType)}/${
                              fundingParentTransaction.TransactionID
                            }/${details.Currency}`,
                            '_blank',
                          );
                        }}
                      >
                        {fundingParentTransaction.TransactionID}
                      </UtilButton>
                    </Box>

                    <TransactionStatus
                      status={GetColorStatus(fundingParentTransaction.TransactionStatus)}
                      label={tGlobal(
                        `Transaction.TransactionHistory.statuses.${fundingParentTransaction.TransactionStatus.toLowerCase()}`,
                        { defaultValue: capitalizeFirstLetterOfWords(fundingParentTransaction.TransactionStatus) },
                      )}
                    />
                  </Box>
                )}

                <Box flx="col" gapSize="6px">
                  <div className={cn('row')}>{t('elinxID', { defaultValue: 'eLinx ID' })}</div>
                  <div className={cn('fontWeight500', 'pt')}>{details.ELinxRequestID}</div>
                </Box>

                {details.ScheduledPaymentsID && (
                  <Box flx="col" gapSize="2px">
                    <div className={cn('row')}>{t('scheduleID', { defaultValue: 'Schedule ID' })}</div>

                    <Box gapSize="5px">
                      <UtilButton
                        onClick={() => {
                          history.push(`/scheduled-transactions/details/${details.ScheduledPaymentsID}`);
                        }}
                      >
                        {details.ScheduledPaymentsID}
                      </UtilButton>
                    </Box>
                  </Box>
                )}

                {details.TransactionID && (
                  <Box flx="col" gapSize="6px" ai="fs">
                    <div className={cn('row')}>{t('transactionID', { defaultValue: 'Transaction ID' })}</div>

                    <Box gapSize="5px">
                      {details.TransactionID.split(',')
                        .map((id) => id.trim())
                        .filter(Boolean)
                        .map((id) => (
                          <UtilButton
                            key={id}
                            className={cn('idButton')}
                            onClick={() => {
                              window.open(`/transactions-history/details/eLinx/${id}/${details.Currency}`, '_blank');
                            }}
                          >
                            {id}
                          </UtilButton>
                        ))}
                    </Box>

                    {transaction && (
                      <TransactionStatus
                        status={GetColorStatus(transaction.TransactionStatus)}
                        label={tGlobal(
                          `Transaction.TransactionHistory.statuses.${transaction.TransactionStatus.toLowerCase()}`,
                          { defaultValue: capitalizeFirstLetterOfWords(transaction.TransactionStatus) },
                        )}
                      />
                    )}
                  </Box>
                )}
              </div>
            </div>

            <div className={cn('linkContainer')}>
              <DetailsRows
                className={cn('detailsRows')}
                details={[
                  {
                    label: t('embeddedLink', { defaultValue: 'eLinx Embedded Link' }),
                    value: (
                      <Box ai="center" gap="sm" jc="fe">
                        {details?.Link}
                        <CopyButton content={details.Link} />
                      </Box>
                    ),
                  },
                ]}
              />
            </div>
          </Box>

          <div className={cn('summaryStepper')}>
            <ElinxStatusStepper status={details.Status} date={details.RequestDate} />
          </div>
        </Box>
      </Card>

      {isScheduled && scheduledTransaction && (
        <Card className={cn('card')}>
          <Box flx="col" gapSize="24px">
            <Title level="4">{t('ScheduledInformation.title', { defaultValue: 'Scheduled Information' })}</Title>

            <DetailsRows
              className={cn('detailsRows')}
              details={[
                {
                  label: t('ScheduledInformation.frequency', { defaultValue: 'Frequency' }),
                  value: capitalizeFirstLetter(scheduledTransaction.Frequency),
                  visible: Boolean(scheduledTransaction.Frequency),
                },
                {
                  label: t('ScheduledInformation.nameOfFrequency', { defaultValue: 'Name of Frequency' }),
                  value:
                    frequencyLabelMapper(scheduledTransaction.NameOfFrequency) ||
                    capitalizeFirstLetterOfWords(scheduledTransaction.NameOfFrequency),
                  visible: Boolean(scheduledTransaction.NameOfFrequency),
                },
                {
                  label: t('ScheduledInformation.endingAfterPayments', { defaultValue: 'Ending After Payments' }),
                  value: scheduledTransaction.EndingAfterPayments,
                  visible: Boolean(scheduledTransaction.EndingAfterPayments),
                },
                {
                  label: t('ScheduledInformation.startDate', { defaultValue: 'Start Date' }),
                  value: scheduledTransaction.StartDate,
                  visible: Boolean(scheduledTransaction.StartDate),
                },
                {
                  label: t('ScheduledInformation.endDate', { defaultValue: 'End Date' }),
                  value: scheduledTransaction.EndDate,
                  visible: Boolean(scheduledTransaction.EndDate),
                },
              ]}
            />
          </Box>
        </Card>
      )}

      {transaction && details.Status === 'completed' && (
        <Card className={cn('card')}>
          <Box flx="col" gapSize="24px">
            <Title level="4">{t('TransactionInformation.title', { defaultValue: 'Transaction Information' })}</Title>

            <DetailsRows
              className={cn('detailsRows')}
              details={[
                {
                  label: t('TransactionInformation.type', { defaultValue: 'Transaction Type' }),
                  value: <TransactionTypeLabel transactionType={transaction.TransactionType || '-'} />,
                },
                {
                  label: t('TransactionInformation.holdAmount', { defaultValue: 'Hold Amount' }),
                  value: transaction.Currency + ' ' + transaction.HoldAmount,
                },
                {
                  label: t('TransactionInformation.convenienceFee', { defaultValue: 'Convenience Fee' }),
                  value: transaction.Currency + ' ' + transaction.ConvenienceFeeAmount,
                },
                {
                  label: t('TransactionInformation.notes', { defaultValue: 'Notes' }),
                  value: transaction.Notes || '-',
                },
              ]}
            />
          </Box>
        </Card>
      )}

      <ApprovalHistoryDetailsCard
        transactionID={details.TransactionID || ''}
        status={statusMap[details.Status]}
        type={TransactionType.ELINX}
      />

      <Card className={cn('card')}>
        <Box flx="col" gapSize="24px">
          <Title level="4">{t('PaymentSetting.title', { defaultValue: 'Contact’s Payment Settings' })}</Title>

          <DetailsRows
            className={cn('detailsRows')}
            details={[
              {
                label: t('PaymentSetting.methodOptions', { defaultValue: 'Payment Method Options' }),
                value: getPaymentMethodDisplayOptions(details.PaymentMethodDisplayOptions),
              },
              {
                label: t('PaymentSetting.methodType', { defaultValue: 'Request Payment Method Type' }),
                value: getPaymentMethod(details.RequestPaymentMethodType) || '-',
              },
              {
                label: t('PaymentSetting.bankAccountRail', { defaultValue: 'Bank Account Payment Rail' }),
                value: getBankingPaymentRail(bankingPaymentRails),
                visible: details.RequestPaymentMethodType.includes('bank'),
              },
              {
                label: t('PaymentSetting.bankConnectionType', { defaultValue: 'Bank Account Connection Type' }),
                value: details.Status === 'completed' ? getBankConnectionType(details.BankAccountConnectionType) : '-',
                visible: details.RequestPaymentMethodType.includes('bank'),
              },
              {
                label: t('PaymentSetting.acceptedCardBrands', { defaultValue: 'Accepted Card Brand(s)' }),
                value: getCardBrand(details.AcceptedCardBrands) || '-',
                visible: showCardSettings,
              },
              {
                label: t('PaymentSetting.cardTypeValidation', { defaultValue: 'Card Type Validation' }),
                value: details.CardTypeValidation
                  ? t('enabled', { defaultValue: 'Enabled' })
                  : t('disabled', { defaultValue: 'Disabled' }),
                visible: showCardSettings,
              },
            ]}
          />
        </Box>
      </Card>

      {flexiblePaymentOptions.includes('multiple') && (
        <Card className={cn('card')}>
          <Box flx="col" gapSize="24px">
            <Title level="4">
              {t('FlexiblePayment.multiplePaymentMethods', { defaultValue: 'Multiple Payment Methods' })}
            </Title>

            <DetailsRows
              className={cn('detailsRows')}
              details={[
                {
                  label: t('FlexiblePayment.multiplePaymentMethods', { defaultValue: 'Multiple Payment Methods' }),
                  value: t('enabled', { defaultValue: 'Enabled' }),
                },
              ]}
            />
          </Box>
        </Card>
      )}

      {flexiblePaymentOptions.includes('partial') && (
        <Card className={cn('card')}>
          <Box flx="col" gapSize="24px">
            <Title level="4">{t('FlexiblePayment.partialPayment', { defaultValue: 'Partial Payment' })}</Title>

            <DetailsRows
              className={cn('detailsRows')}
              details={[
                {
                  label: t('FlexiblePayment.minimumPayment', { defaultValue: 'Minimum Partial Payment' }),
                  value: details.MinimumPaymentAccepted || '-',
                },
                {
                  label: t('FlexiblePayment.remainingAmountMandatory', {
                    defaultValue: 'Schedule Remaining Amount Mandatory',
                  }),
                  value:
                    details.RemainderPaymentDate !== ''
                      ? t('enabled', { defaultValue: 'Enabled' })
                      : t('disabled', { defaultValue: 'Disabled' }),
                  visible: details.RemainderPaymentDate !== '',
                },
                {
                  label: t('FlexiblePayment.remainingAmountDate', { defaultValue: 'Remaining Amount Date' }),
                  value: details.RemainderPaymentDate,
                  visible: details.RemainderPaymentDate !== '',
                },
              ]}
            />
          </Box>
        </Card>
      )}

      {flexiblePaymentOptions.includes('installments') && (
        <Card className={cn('card')}>
          <Box flx="col" gapSize="24px">
            <Title level="4">{t('FlexiblePayment.installmentsPlan', { defaultValue: 'Installment Plan' })}</Title>

            <DetailsRows
              className={cn('detailsRows')}
              details={[
                {
                  label: t('FlexiblePayment.numOfInstallments', { defaultValue: 'Number of Installments' }),
                  value: details.NumberOfInstallments || '-',
                },
                {
                  label: t('FlexiblePayment.frequency', { defaultValue: 'Frequency' }),
                  value: t(`FlexiblePayment.${details.InstallmentFrequency}`),
                },
              ]}
            />
          </Box>
        </Card>
      )}

      <Card className={cn('card')}>
        <Box flx="col" gapSize="24px">
          <Title level="4">{t('ElinxDetails.title', { defaultValue: 'eLinx Details' })}</Title>

          <DetailsRows
            className={cn('detailsRows')}
            details={[
              {
                label: t('ElinxDetails.requestedDate', { defaultValue: 'Requested Date' }),
                value: details.RequestDate || '-',
              },
              {
                label: t('ElinxDetails.type', { defaultValue: 'Type' }),
                value: details.ELinxRequestType ? capitalizeFirstLetter(details.ELinxRequestType) : '-',
              },
              {
                label: t('ElinxDetails.sentBy', { defaultValue: 'Sent by' }),
                value: details.SenderName || '-',
              },
              {
                label: (
                  <div className={cn('row')}>
                    {t('ElinxDetails.messageToContact', { defaultValue: 'Message to Contact' })}
                  </div>
                ),
                value: details.RecipientMessage === '' ? '-' : details.RecipientMessage,
              },
              {
                label: t('ElinxDetails.invoiceNumber', { defaultValue: 'Invoice Number' }),
                value: details.InvoiceNumber,
                visible: details.InvoiceNumber !== '',
              },
              {
                label: t('ElinxDetails.invoice', { defaultValue: 'Invoice' }),
                value: (
                  <UtilButton
                    size="medium"
                    iconRightProps={{
                      icon: 'visibility',
                      fillAmount: 1,
                    }}
                    onClick={() => {
                      window.open(documentLink, '_blank');
                    }}
                  >
                    {documentName || `${t('ElinxDetails.invoice')}`}
                  </UtilButton>
                ),
                visible: documentLink !== '',
              },
            ]}
          />
        </Box>
      </Card>

      <Card className={cn('card')}>
        <Box flx="col" gapSize="24px">
          <Title level="4">{t('ElinxSettings.title', { defaultValue: 'eLinx Settings' })}</Title>

          <DetailsRows
            className={cn('detailsRows')}
            details={[
              {
                label: t('ElinxSettings.notification', { defaultValue: 'Recipient Notification' }),
                value: details.NotificationEnabled
                  ? t('enabled', { defaultValue: 'Enabled' })
                  : t('disabled', { defaultValue: 'Disabled' }),
              },
              {
                label: t('ElinxSettings.reminder', { defaultValue: 'Recipient Reminder' }),
                value: details.ReminderEnabled
                  ? t('enabled', { defaultValue: 'Enabled' })
                  : t('disabled', { defaultValue: 'Disabled' }),
              },
              {
                label: t('ElinxSettings.reminderFreqency', { defaultValue: 'Reminder Frequency (days)' }),
                value: details.ReminderFrequency || '-',
              },
              {
                label: t('ElinxSettings.notificationVia', { defaultValue: 'Send Notifications via' }),
                value: (
                  <div className={cn(details.NotificationMethod === 'sms' ? 'uppercase' : 'capitalize')}>
                    {details.NotificationMethod}
                  </div>
                ),
              },
              {
                label: t('ElinxSettings.additionalEmail', { defaultValue: 'Additional Recipients Email' }),
                value: details.Recipients.filter((email) => email !== details.ReceiverEmailAddress).join(', ') || '-',
                visible: details.Recipients.length > 0,
              },
            ]}
          />
        </Box>
      </Card>

      <Card className={cn('card')}>
        <Box flx="col" gapSize="24px">
          <Title level="4">{t('RecipientInformation.title', { defaultValue: 'Recipient Information' })}</Title>

          <DetailsRows
            className={cn('detailsRows')}
            details={[
              {
                label: t('RecipientInformation.recipientName', { defaultValue: 'Recipient Name' }),
                value: (
                  <UtilButton
                    onClick={() => {
                      if (clientAccountsEnabled) {
                        history.push(`/client-accounts/${details.ClientAccountID}`);

                        onSubNavClick(
                          'clientAccountsList',
                          `/client-accounts/${details.ClientAccountID}`,
                          t('accountHolderDetails.clientAccountsList'),
                        );
                      } else {
                        history.push(`/contacts/${details.ContactID}`);

                        onSubNavClick(
                          'contactAccounts',
                          `/contacts/${details.ContactID}`,
                          t('accountHolderDetails.contactList'),
                        );
                      }
                    }}
                  >
                    {details.ReceiverName}
                  </UtilButton>
                ),
              },
              {
                label: t('RecipientInformation.contactName', { defaultValue: 'Contact Type' }),
                value: getAccountType(details.ReceiverContactType),
              },
              {
                label: t('RecipientInformation.emailAddress', { defaultValue: 'Email Address' }),
                value: details.ReceiverEmailAddress || '-',
              },
              {
                label: t('RecipientInformation.phoneNumber', { defaultValue: 'Phone Number' }),
                value: (
                  <div className={cn('capitalize')}>
                    {formatPhoneNumberWithParentheses(details.ReceiverPhoneNumber || '') || '-'}
                  </div>
                ),
              },
            ]}
          />
        </Box>
      </Card>
    </>
  );

  function getAccountType(type: AccountType) {
    const map: Record<AccountType, string> = {
      individual: t('RecipientInformation.individual', { defaultValue: 'Individual' }),
      business: t('RecipientInformation.business', { defaultValue: 'Business' }),
      'receive-only': t('RecipientInformation.receiveOnly', { defaultValue: 'Receive-Only' }),
      unverified: t('RecipientInformation.unverified', { defaultValue: 'Unverified' }),
      standard: t('RecipientInformation.standard', { defaultValue: 'Standard' }),
    };

    return map[type];
  }

  function getBankConnectionType(type: BankAccountConnectionType) {
    const map: Record<BankAccountConnectionType, string> = {
      manual: t(`PaymentSetting.manual`, { defaultValue: 'Manual' }),
      flinks: t(`PaymentSetting.flinks`, { defaultValue: 'Flinks' }),
      plaid: t(`PaymentSetting.plaid`, { defaultValue: 'Plaid' }),
      fiserv: t(`PaymentSetting.fiserv`, { defaultValue: 'Fiserv' }),
      inverite: t(`PaymentSetting.inverite`, { defaultValue: 'Inverite' }),
      mx: t(`PaymentSetting.mx`, { defaultValue: 'MX' }),
    };

    return map[type];
  }

  function getPaymentMethodDisplayOptions(option: PaymentMethodDisplayOptions) {
    const map: Record<PaymentMethodDisplayOptions, string> = {
      New: t('PaymentSetting.newPayment', { defaultValue: 'Request New Payment Methods' }),
      Existing: t('PaymentSetting.existingPayment', { defaultValue: 'Existing Payment Methods' }),
      Both: t('PaymentSetting.bothPayment', { defaultValue: 'Existing Payment Methods & Request New Payment Methods' }),
    };

    return map[option];
  }

  function getBankingPaymentRail(rails: BankingPaymentRail[]) {
    const map: Record<BankingPaymentRail, string> = {
      eft: t('PaymentSetting.eft', { defaultValue: 'Electronic Funds Transfer' }),
      vopayinstant: t('PaymentSetting.vopayInstant', { defaultValue: 'VoPay Instant' }),
      ach: t('PaymentSetting.ach', { defaultValue: 'Automated Clearing House' }),
      rtp: t('PaymentSetting.rtp', { defaultValue: 'Real-time Transport Protocol' }),
      fedwire: t('PaymentSetting.wire', { defaultValue: 'Wire' }),
    };

    return rails.map((rail) => map[rail]).join(', ');
  }

  function getPaymentMethod(methods: string) {
    const map: Record<string, React.ReactNode> = {
      bank: t('PaymentSetting.bankAccount', { defaultValue: 'Bank Account' }),
      credit: t('PaymentSetting.creditCard', { defaultValue: 'Credit Card' }),
      email: <InteracWordmark />,
      debitcard: t('PaymentSetting.debitCard', { defaultValue: 'Debit Card' }),
      googlepay: t('PaymentSetting.googlePay', { defaultValue: 'Google Pay' }),
      applepay: t('PaymentSetting.applePay', { defaultValue: 'Apple Pay' }),
      paypal: t('PaymentSetting.payPal', { defaultValue: 'PayPal' }),
      venmo: t('PaymentSetting.venmo', { defaultValue: 'Venmo' }),
      zelle: t('PaymentSetting.zelle', { defaultValue: 'Zelle' }),
    };

    const list = methods
      .split(',')
      .map((m) => map[m.trim()])
      .filter(Boolean);

    return list.map((item, idx) => (
      <span key={idx}>
        {item}
        {idx < list.length - 1 ? ', ' : ''}
      </span>
    ));
  }

  function getCardBrand(brands: AcceptedCardBrand[]) {
    const brandMap: Record<AcceptedCardBrand, string> = {
      visa: t('PaymentSetting.visa', { defaultValue: 'Visa' }),
      mastercard: t('PaymentSetting.mastercard', { defaultValue: 'Mastercard' }),
      discover: t('PaymentSetting.discover', { defaultValue: 'Discover' }),
      amex: t('PaymentSetting.amex', { defaultValue: 'AMEX' }),
      unionpay: t('PaymentSetting.unionpay', { defaultValue: 'UnionPay' }),
    };

    return brands.map((brand) => brandMap[brand]).join(', ');
  }
};

export default ElinxDetails;
